<script>
import controller from '../../scripts/controller/controller';

export default {
	props: {
		// link to load data from (String)
		// or data object itself (Object)
		dataSource: {
			type: [Object, String],
			default: undefined
		},

		// Bitrix component id
		// required by Bitrix to determine component instance for AJAX requests
		// (dataSource link often simpty refers to page containing multiple components)
		bxajaxid: {
			type: String,
			default: undefined
		},

		// immediately load data on "created" hook
		// if dataSource is a link
		loadOnInit: {
			type: Boolean,
			default: true
		}
	},

	data() {
		return {
			// bxajaxid which is finally computed after processing props and slots
			localBxajaxid: null,

			// Only the newest request may update the component state. Filter requests
			// can finish out of order when a user changes values quickly.
			latestDataLoadId: 0,

			// loading state
			loading: false
		};
	},

	computed: {
		componentName() {
			let path = this.$options.__file;
			if (path) {
				return path.slice(path.lastIndexOf('/') + 1, path.lastIndexOf('.'));
			}
			return null;
		}
	},

	watch: {
		dataSource() {
			// try to load using props
			this.init(this.bxajaxid, this.dataSource);
		}
	},

	created() {
		// try to load using props
		this.init(this.bxajaxid, this.dataSource);

		const readSlot = (item) => {
			const bxajaxid = item.props['bxajaxid'];
			const dataSource = item.props['data-source'];

			if (dataSource) {
				this.init(bxajaxid, dataSource);
			}
		};

		// try to load using slots
		if (this.$slots?.source) {
			this.$slots?.source().forEach((item) => {
				readSlot(item);
				item.children?.forEach(readSlot);
			});
		}
	},

	methods: {
		dataSet(data, options) {
			console.info(`${this.componentName}: warning: v-source-mixin is used but sourceSet is not implemented in your component`, data, options);
		},

		requestWithUploadProgress(url, data, options = {}) {
			return new Promise((resolve, reject) => {
				const xhr = new XMLHttpRequest();
				xhr.open('POST', url, true);
				xhr.timeout = Number(options.timeout) || 180000;
				xhr.upload.addEventListener('progress', (event) => {
					if (event.lengthComputable) {
						options.onUploadProgress?.(Math.round((event.loaded / event.total) * 100), event);
					}
				});
				xhr.upload.addEventListener('load', () => options.onUploadComplete?.());
				xhr.addEventListener('load', () => {
					resolve({
						ok: xhr.status >= 200 && xhr.status < 300,
						status: xhr.status,
						json: async () => JSON.parse(xhr.responseText)
					});
				});
				const fail = (type) => {
					const error = new Error(type === 'timeout' ? 'Request timed out' : 'Network request failed');
					error.type = type;
					error.xhr = xhr;
					reject(error);
				};
				xhr.addEventListener('error', () => fail('network'));
				xhr.addEventListener('timeout', () => fail('timeout'));
				xhr.addEventListener('abort', () => fail('abort'));
				xhr.send(data);
			});
		},

		async dataLoad(options = {}, dataSetOptions) {
			const requestId = ++this.latestDataLoadId;

			// use this.loading to set preloaders if needed
			this.loading = true;

			// check arguments
			if (!options || typeof options !== 'object' || !options.url) {
				console.warn(`${this.componentName}: warning: dataLoad options arg is wrong`, options);
			}

			// convert any options.data to transfer as FormData
			let formattedData = new FormData();

			if (options.data instanceof FormData) {
				formattedData = options.data;
			} else if (options.data) {
				let makeFormData = (data, parentKey) => {
					if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
						Object.keys(data).forEach((key) => {
							makeFormData(data[key], parentKey ? `${parentKey}[${key}]` : key);
						});
					} else {
						formattedData.append(parentKey, data == null ? '' : data);
					}
				};

				makeFormData(options.data);
			}

			// set bxajaxid if present
			if (this.localBxajaxid) {
				formattedData.set('bxajaxid', this.localBxajaxid);
			}

			// set min delay to ensure preloader fadein/fadeout plays fine (<transition name="fade"> speed is 250ms)
			const delayPromise = new Promise((resolve) => {
				options.delay ? setTimeout(resolve, 250) : resolve();
			});

			try {
				const maxRetries = Math.max(0, Number(options.retryOnServerError) || 0);
				let response;

				// Some filter requests may fail transiently during a deploy or under load.
				// Components can explicitly opt in to retrying server errors.
				for (let attempt = 0; attempt <= maxRetries; attempt++) {
					response = options.trackUploadProgress
						? await this.requestWithUploadProgress(options.url, formattedData, options)
						: await fetch(options.url, {
							method: 'POST',
							body: formattedData
						});

					if (response.ok || response.status < 500 || attempt === maxRetries) {
						break;
					}

					await new Promise((resolve) => setTimeout(resolve, 300));
				}

				await delayPromise;

				// handle response
				if (!response.ok) {
					if (requestId === this.latestDataLoadId) {
						console.warn(`${this.componentName}: AJAX request failed`, response);
						options.onError?.(response);
					}
				} else {
					let data = await response.json();
					if ((typeof (data.encoded) !== 'undefined')) {
						data = JSON.parse(atob(data.encoded));
					}

					// A newer request has already started, so applying this response would
					// restore an outdated filter value and make checkboxes jump.
					if (requestId !== this.latestDataLoadId) {
						return;
					}

					console.log(`${this.componentName}: AJAX request completed`, data);

					if (data.redirect) {
						window.location.href = data.redirect;
						if(!data.stayModalOpen) {
							setTimeout(() => {
								controller.closeAllModals();
							}, 500)
						}
					}

					if (data.reload) {
						window.location.reload(true);
					}

					if (data.displayURL) {
						window.history.replaceState(null, null, data.displayURL);
					}

					this.dataSet(data, dataSetOptions);
					options.onSuccess?.(data);
				}
			} catch (e) {
				if (requestId === this.latestDataLoadId) {
					console.warn(`${this.componentName}: AJAX request failed`, e);
					options.onError?.(e);
				}
			} finally {
				if (requestId === this.latestDataLoadId) {
					this.loading = false;
				}
			}
		},

		init(bxajaxid, dataSource) {
			if (typeof bxajaxid !== 'undefined') {
				this.localBxajaxid = bxajaxid;
			}

			if (typeof dataSource === 'string') {
				if (this.loadOnInit) {
					this.dataLoad({url: dataSource});
				}
			} else if (typeof dataSource !== 'undefined') {
				if ((typeof (dataSource.encoded) !== 'undefined')){
					dataSource = JSON.parse(atob(dataSource.encoded));
				}
				this.dataSet(dataSource);
			}
		}
	}
};
</script>
